import { action, computed, observable } from 'mobx';
import { type AuthApi } from '@linktivity/ars-neo';
import { sleep, type User, type UserCredential } from '@linktivity/link-utils';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  MAX_REFRESH_COUNT,
  TIMEOUT
} from '@link/share/constants';

const storage = localStorage;
const TOKEN_REFRESH_THRESHOLD_SECONDS = 3600;
const REVOKE_TOKEN_TIMEOUT = 3000;

export interface Claims {
  role: string;
  supplier: string;
}

export default abstract class Auth {
  @observable accessor user: User | null = null;
  // Whether Firebase has restored the signed-in user, or found there is none.
  @observable accessor authReady = false;
  @observable accessor claims: Claims | null = null;
  @observable accessor accessToken = storage.getItem(ACCESS_TOKEN) || '';
  @observable accessor refreshToken = storage.getItem(REFRESH_TOKEN) || '';

  private _refreshingPromise: Promise<string | undefined> | null = null;

  protected abstract get authApi(): AuthApi;

  @action
  setAccessToken(accessToken?: string) {
    if (accessToken) {
      this.accessToken = accessToken;
      storage.setItem(ACCESS_TOKEN, accessToken);
    }
  }

  getAccessToken() {
    return this.accessToken;
  }

  @action
  setRefreshToken(refreshToken?: string) {
    if (refreshToken) {
      this.refreshToken = refreshToken;
      storage.setItem(REFRESH_TOKEN, refreshToken);
    }
  }

  isTokenExpiringSoon(): boolean {
    const token = this.accessToken;
    if (!token) return false;
    try {
      const segment = token.split('.')[1];
      if (!segment) return false;
      const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
      const base64Payload = padded.padEnd(
        padded.length + ((4 - (padded.length % 4)) % 4),
        '='
      );
      const payload = JSON.parse(window.atob(base64Payload)) as {
        exp?: number;
      };
      if (typeof payload.exp !== 'number') return false;
      return payload.exp - Date.now() / 1000 < TOKEN_REFRESH_THRESHOLD_SECONDS;
    } catch {
      return false;
    }
  }

  async refreshExpiredToken(): Promise<string | undefined> {
    if (this._refreshingPromise) {
      return this._refreshingPromise;
    }
    const promise = this.authApi
      .refreshToken({
        body: {
          refreshToken: this.refreshToken
        }
      })
      .then(res => {
        if (res.accessToken) {
          this.setAccessToken(res.accessToken);
        }
        if (res.refreshToken) {
          this.setRefreshToken(res.refreshToken);
        }
        return res.accessToken;
      })
      .finally(() => {
        this._refreshingPromise = null;
      });
    this._refreshingPromise = promise;
    return promise;
  }

  // Revoke the tokens server-side on logout. Kept awaited so the request still
  // carries a valid access token, but time-boxed so a stalled call never hangs
  // sign-out; failures are ignored since the local session is cleared anyway.
  async revokeToken(): Promise<void> {
    const { accessToken, refreshToken } = this;
    if (!accessToken && !refreshToken) {
      return;
    }
    try {
      await this.authApi.revokeToken(
        { body: { accessToken, refreshToken } },
        { signal: AbortSignal.timeout(REVOKE_TOKEN_TIMEOUT) }
      );
    } catch {
      // Ignore: revoking is best-effort.
    }
  }

  @action
  setAuthReady() {
    this.authReady = true;
  }

  @action
  setUser(user: User) {
    this.user = user;
    this.getClaims(user);
  }

  @action
  clearUser() {
    this.user = null;
    this.clearTokens();
  }

  @action
  async getClaims(user: User) {
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult) {
      this.setClaims(tokenResult.claims as unknown as Claims);
    }
  }

  @action
  setClaims(claims: Claims) {
    this.claims = claims;
  }

  @action
  setToken(token: string) {
    try {
      storage.setItem(ACCESS_TOKEN, token);
    } finally {
      this.accessToken = token;
    }
  }

  @action
  clearTokens() {
    try {
      storage.removeItem(ACCESS_TOKEN);
      storage.removeItem(REFRESH_TOKEN);
    } finally {
      this.accessToken = '';
      this.refreshToken = '';
    }
  }

  @action
  async getToken() {
    if (this.user) {
      return this.user.getIdToken();
    } else {
      return '';
    }
  }

  @action
  getRefreshedToken = async () => {
    let count = 0;
    const requestToken: () => Promise<string> = async () => {
      const token = await this.getToken();
      if (token) {
        return token;
      } else {
        if (count < MAX_REFRESH_COUNT) {
          count++;
          await sleep(TIMEOUT);
          return await requestToken();
        } else {
          return '';
        }
      }
    };
    return await requestToken();
  };

  @action
  updateToken(
    credential: UserCredential,
    _recaptchaToken?: string
  ): void | Promise<void> {
    this.setUser(credential.user);
  }

  @computed
  get loggedIn(): boolean {
    return !!this.accessToken;
  }

  @computed
  get isPasswordAuth(): boolean {
    return this.user?.providerData.some(
      provider => provider.providerId === 'password'
    )
      ? true
      : false;
  }
}
